import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReminderEngineChannelInfo, ReminderEngineDefinition, ReminderEngineSubjectParameter, ReminderEngineSubjectType, UpsertReminderDefinitionRequest } from '../types/ReminderEngine';
import type { MessageTemplate } from '../../conversations/services/conversationService';
import {
  categoryToSubjectType,
  emptyRule,
  subjectTypeToCategory,
  toRuleFormState,
  toUpsertPayload,
  type RuleFormState,
} from '../utils/definitionEditorForm';
import { findMessageTemplate, pickDefaultTemplateKey } from '../utils/messageTemplateDefaults';
import { listReminderSubjectParameters } from '../services/reminderEngineService';
import { RuleCriteriaEditor } from './RuleCriteriaEditor';
import formStyles from './DefinitionEditorForm.module.css';
import sharedStyles from './reminderEngineShared.module.css';

type DefinitionEditorFormProps = {
  definition: ReminderEngineDefinition;
  channels: ReminderEngineChannelInfo[];
  messageTemplates: MessageTemplate[];
  subjectTypes: ReminderEngineSubjectType[];
  subjectParameters: ReminderEngineSubjectParameter[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (payload: UpsertReminderDefinitionRequest) => Promise<void>;
};

export const DefinitionEditorForm: React.FC<DefinitionEditorFormProps> = ({
  definition,
  channels,
  messageTemplates,
  subjectTypes,
  subjectParameters: initialSubjectParameters,
  busy,
  error,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation(['reminderEngine', 'common']);
  const defaultChannelKey = channels[0]?.key ?? '';
  const initialSubjectType = categoryToSubjectType(definition.category ?? '') || subjectTypes[0]?.subjectType || '';
  const [name, setName] = React.useState(definition.name);
  const [subjectType, setSubjectType] = React.useState(initialSubjectType);
  const [subjectParameters, setSubjectParameters] = React.useState(initialSubjectParameters);
  const [rules, setRules] = React.useState<RuleFormState[]>(() => toRuleFormState(definition));
  const defaultTemplateKey = pickDefaultTemplateKey(subjectTypeToCategory(subjectType), messageTemplates);

  React.useEffect(() => {
    setSubjectParameters(initialSubjectParameters);
  }, [initialSubjectParameters]);

  React.useEffect(() => {
    if (!subjectType) {
      setSubjectParameters([]);
      return;
    }

    let cancelled = false;
    void listReminderSubjectParameters(subjectType)
      .then((parameters) => {
        if (!cancelled) {
          setSubjectParameters(parameters);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSubjectParameters([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [subjectType]);

  React.useEffect(() => {
    setName(definition.name);
    setSubjectType(categoryToSubjectType(definition.category ?? '') || subjectTypes[0]?.subjectType || '');
    setRules(toRuleFormState(definition));
  }, [definition, subjectTypes]);

  const updateRule = (index: number, patch: Partial<RuleFormState>) => {
    setRules((current) => current.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, ...patch } : rule)));
  };

  const updateRuleChannel = (
    ruleIndex: number,
    channelIndex: number,
    patch: Partial<RuleFormState['channels'][number]>,
  ) => {
    setRules((current) =>
      current.map((rule, currentRuleIndex) => {
        if (currentRuleIndex !== ruleIndex) {
          return rule;
        }

        const nextChannels = rule.channels.map((channel, currentChannelIndex) =>
          currentChannelIndex === channelIndex ? { ...channel, ...patch } : channel,
        );
        return { ...rule, channels: nextChannels };
      }),
    );
  };

  const addRule = () => {
    setRules((current) => [...current, emptyRule(current.length + 1, defaultChannelKey, defaultTemplateKey)]);
  };

  const removeRule = (index: number) => {
    setRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index));
  };

  const addChannel = (ruleIndex: number) => {
    setRules((current) =>
      current.map((rule, currentRuleIndex) =>
        currentRuleIndex === ruleIndex
          ? {
              ...rule,
              channels: [...rule.channels, { channelKey: defaultChannelKey, templateKey: defaultTemplateKey, priority: rule.channels.length }],
            }
          : rule,
      ),
    );
  };

  const removeChannel = (ruleIndex: number, channelIndex: number) => {
    setRules((current) =>
      current.map((rule, currentRuleIndex) =>
        currentRuleIndex === ruleIndex
          ? { ...rule, channels: rule.channels.filter((_, currentChannelIndex) => currentChannelIndex !== channelIndex) }
          : rule,
      ),
    );
  };

  const handleSave = async () => {
    await onSave(toUpsertPayload(definition, name, subjectTypeToCategory(subjectType), rules));
  };

  return (
    <div className={formStyles.formStack}>
      {error && <p className={sharedStyles.error}>{error}</p>}

      <div className={formStyles.metaBanner}>
        <span><strong>{t('key')}:</strong> <code>{definition.key}</code></span>
        <span><strong>{t('version')}:</strong> {definition.version}</span>
        <span><strong>{t('status')}:</strong> {definition.status}</span>
      </div>

      <div className={formStyles.fieldGrid}>
        <label className={formStyles.fieldLabel}>
          {t('name')}
          <input className="text-input" value={name} onChange={(event) => setName(event.target.value)} disabled={busy} />
        </label>
        <label className={formStyles.fieldLabel}>
          {t('subjectType')}
          <select
            className="text-input"
            value={subjectType}
            onChange={(event) => setSubjectType(event.target.value)}
            disabled={busy}
          >
            {subjectTypes.map((item) => (
              <option key={item.subjectType} value={item.subjectType}>{item.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className={formStyles.rulesHeader}>
        <h4>{t('rules')}</h4>
        <button type="button" className="btn btn-secondary btn-sm" onClick={addRule} disabled={busy}>
          {t('addRule')}
        </button>
      </div>

      {rules.map((rule, ruleIndex) => (
        <div key={`rule-${ruleIndex}`} className={formStyles.ruleCard}>
          <div className={formStyles.ruleHeader}>
            <strong>{t('ruleNumber', { number: ruleIndex + 1 })}</strong>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeRule(ruleIndex)} disabled={busy || rules.length <= 1}>
              {t('removeRule')}
            </button>
          </div>

          <div className={formStyles.fieldGrid}>
            <label className={formStyles.fieldLabel}>
              {t('ruleName')}
              <input
                className="text-input"
                value={rule.name}
                onChange={(event) => updateRule(ruleIndex, { name: event.target.value })}
                disabled={busy}
              />
            </label>
            <label className={formStyles.fieldLabel}>
              {t('sortOrder')}
              <input
                className="text-input"
                type="number"
                min={1}
                value={rule.sortOrder}
                onChange={(event) => updateRule(ruleIndex, { sortOrder: Number(event.target.value) })}
                disabled={busy}
              />
            </label>
            <label className={formStyles.fieldLabel}>
              {t('offsetValue')}
              <input
                className="text-input"
                type="number"
                min={0}
                value={rule.offsetValue}
                onChange={(event) => updateRule(ruleIndex, { offsetValue: Number(event.target.value) })}
                disabled={busy}
              />
            </label>
            <label className={formStyles.fieldLabel}>
              {t('offsetUnit')}
              <select
                className="text-input"
                value={rule.offsetUnit}
                onChange={(event) => updateRule(ruleIndex, { offsetUnit: event.target.value as RuleFormState['offsetUnit'] })}
                disabled={busy}
              >
                <option value="hour">{t('hour')}</option>
                <option value="day">{t('day')}</option>
              </select>
            </label>
            <label className={formStyles.fieldLabel}>
              {t('anchor')}
              <select
                className="text-input"
                value={rule.anchor}
                onChange={(event) => updateRule(ruleIndex, { anchor: event.target.value as RuleFormState['anchor'] })}
                disabled={busy}
              >
                <option value="beforeSubject">{t('beforeSubject')}</option>
                <option value="onSubject">{t('onSubject')}</option>
                <option value="onCreate">{t('onCreate')}</option>
              </select>
            </label>
          </div>

          <RuleCriteriaEditor
            subjectType={subjectType}
            parameters={subjectParameters}
            criteria={rule.criteria}
            busy={busy}
            onChange={(criteria) => updateRule(ruleIndex, { criteria })}
          />

          <div className={formStyles.channelsSection}>
            <div className={formStyles.channelsHeader}>
              <span>{t('deliveryChannels')}</span>
              <div className={formStyles.channelsHeaderActions}>
                <Link to="/reminder-engine/templates" className={formStyles.manageTemplatesLink}>
                  {t('manageTemplates')}
                </Link>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addChannel(ruleIndex)} disabled={busy || !defaultTemplateKey}>
                  {t('addChannel')}
                </button>
              </div>
            </div>

            {messageTemplates.length === 0 ? (
              <p className={formStyles.templatesEmptyHint}>
                {t('noTemplatesForRules')}{' '}
                <Link to="/reminder-engine/templates">{t('createTemplate')}</Link>.
              </p>
            ) : null}

            {rule.channels.map((channel, channelIndex) => {
              const selectedTemplate = findMessageTemplate(messageTemplates, channel.templateKey);
              const templateMissing = Boolean(channel.templateKey) && !selectedTemplate;

              return (
              <div key={`rule-${ruleIndex}-channel-${channelIndex}`} className={formStyles.channelBlock}>
                <div className={formStyles.channelRow}>
                <select
                  className="text-input"
                  value={channel.channelKey}
                  onChange={(event) => updateRuleChannel(ruleIndex, channelIndex, { channelKey: event.target.value })}
                  disabled={busy}
                  aria-label={t('channel')}
                >
                  {channels.map((item) => (
                    <option key={item.key} value={item.key}>{item.key}</option>
                  ))}
                </select>
                <select
                  className="text-input"
                  value={channel.templateKey}
                  onChange={(event) => updateRuleChannel(ruleIndex, channelIndex, { templateKey: event.target.value })}
                  disabled={busy || messageTemplates.length === 0}
                  aria-label={t('messageTemplate')}
                >
                  {!channel.templateKey ? (
                    <option value="">{t('selectMessageTemplate')}</option>
                  ) : null}
                  {templateMissing ? (
                    <option value={channel.templateKey}>{channel.templateKey} ({t('missingTemplate')})</option>
                  ) : null}
                  {messageTemplates.map((template) => (
                    <option key={template.id} value={template.name}>{template.name}</option>
                  ))}
                </select>
                <input
                  className="text-input"
                  type="number"
                  min={0}
                  value={channel.priority}
                  onChange={(event) => updateRuleChannel(ruleIndex, channelIndex, { priority: Number(event.target.value) })}
                  disabled={busy}
                  aria-label={t('priority')}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => removeChannel(ruleIndex, channelIndex)}
                  disabled={busy || rule.channels.length <= 1}
                >
                  {t('removeChannel')}
                </button>
                </div>
                {selectedTemplate ? (
                  <p className={formStyles.templatePreview}>{selectedTemplate.content}</p>
                ) : templateMissing ? (
                  <p className={formStyles.templatePreviewWarning}>{t('missingTemplateHint')}</p>
                ) : null}
              </div>
            );
            })}
          </div>
        </div>
      ))}

      <div className={formStyles.footerBar}>
        <p className={formStyles.saveHint}>{t('saveCreatesDraft')}</p>
        <div className={formStyles.footerActions}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            {t('cancel', { ns: 'common' })}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void handleSave()} disabled={busy || !name.trim()}>
            {busy ? t('saving', { ns: 'common' }) : t('save', { ns: 'common' })}
          </button>
        </div>
      </div>
    </div>
  );
};
